/**
 * B-side homepage copy, transcribed from the marketing comp
 * (bambee-homepage-bambeefied.html) with no edits.
 *
 * All copy lives here rather than in templates so marketing can revise wording
 * without touching component code, matching how the control homepage holds its
 * content in modules/HomePage/constants/index.js.
 *
 * Terminology in here — Continuous Compliance(tm), Company Coverage(tm),
 * Organizational Matters(tm), Bright Compliance, Smart Handbook, Good Company,
 * Smart Path, Better Teams, Super Support — is not on the approved terminology
 * list and needs brand sign-off before this ships. See BIO-1643.
 */

import jordanImage from '~/assets/images/splash/jordan_c.png';
import judithImage from '~/assets/images/splash/judith_c.png';
import katherineImage from '~/assets/images/splash/katherine_c.png';
import raymondImage from '~/assets/images/splash/raymond_c.png';

/**
 * The Bambee team shown under the hero. Photos are transparent cutouts of
 * varying height, so the row bottom-aligns them and they stand on the green
 * stripe rather than sitting in fixed-height boxes.
 *
 * These are real staff attributions. Confirm names, roles, and photo rights
 * before this reaches production — see BIO-1643.
 */
export const ManagerRow = [
  {
    name: 'Raymond S.',
    title: 'HR Coverage Advisor',
    image: raymondImage,
  },
  {
    name: 'Katherine G.',
    title: 'Chief of Staff',
    image: katherineImage,
  },
  {
    name: 'Jordan W.',
    title: 'HR Manager',
    image: jordanImage,
  },
  {
    name: 'Judith P.',
    title: 'Staff Coordinator',
    image: judithImage,
  },
];

/**
 * B-side hero copy, from the comp's hero block.
 *
 * The hero frame is shared with the control, so these are passed in as props
 * rather than replacing modules/HomePage/constants HeroContent. The control
 * keeps its own copy and its $9/mo, which is what we actually sell today.
 *
 * No plan price here. The hero's plan link falls back to the control's label
 * until the B-side plans section returns with real Stripe products.
 */
export const BSideHeroContent = {
  title: 'Put Your HR on Autopilot',
  subtitle: 'Know your company is always compliant.',
  benefits: [
    { text: 'Your Own Dedicated HR Manager' },
    { text: 'Your Whole Company, Covered' },
    { text: 'Compliance Gaps Found, Fixed & Verified' },
    { text: 'Every Employee Issue Handled' },
    { text: 'A Smart Handbook, Always Current' },
    { text: 'Managers Managed, People Supported' },
    { text: 'Work Off Your Plate & Peace of Mind!' },
  ],
};

/**
 * Qualifier reasons for the B-side, taken from the comp's "What brought you to
 * Bambee?" select.
 *
 * Deliberately a separate list rather than a replacement. The control's nine
 * options stay exactly as they are: ReasonDropdown renders in three shared
 * places (the registration wizard, RegistrationPrompt, BaseHero), and the
 * selected string is persisted to the company record as
 * `what_brought_you_to_bambee` and tracked to Segment. Swapping it globally
 * would move the control mid-experiment and split every dashboard that groups
 * on that field.
 *
 * The comp's list is a reduction: nine options to five, plainer language, and
 * it drops Terminations, Wage and Hour, Recruiting, and Other. Losing "Other"
 * means anyone who does not fit is pushed into a bucket that is wrong for them,
 * which is worth a look before this ships. See BIO-1643.
 */
export const BSideReasonOptions = [
  'Employee Onboarding & Training',
  'Staying compliant',
  'A performance or employee issue',
  'Policies & my handbook',
  'I want HR handled for me',
];

// Section 1 — Why Bambee exists
export const WhyBambee = {
  eyebrow: 'Why Bambee exists',
  heading: 'The moment you hire people, you create an entirely new operation.',
  body: [
    'Running the business is the job you signed up for. Running the workforce is the job that shows up the day you hire: laws, policies, managers, people, problems.',
    'Most companies never build that operation on purpose. Pieces of it scatter across the founder, the managers, and whoever happens to remember.',
  ],
  emphasis:
    'Bambee runs more and more of it for you, as much as you hand over.',
  operations: [
    {
      kind: 'biz',
      label: 'The business operation',
      title: 'Product',
      body: 'Build what the company sells.',
    },
    {
      kind: 'work',
      label: 'The workforce operation',
      title: 'Regulation',
      body: 'Know what the company must do.',
    },
    {
      kind: 'biz',
      label: 'The business operation',
      title: 'Customers',
      body: 'Create and retain demand.',
    },
    {
      kind: 'work',
      label: 'The workforce operation',
      title: 'People',
      body: 'Support the humans operating inside it.',
    },
    {
      kind: 'biz',
      label: 'The business operation',
      title: 'Revenue',
      body: 'Produce economic value.',
    },
    {
      kind: 'work',
      label: 'The workforce operation',
      title: 'Management',
      body: 'Make expectations and accountability real.',
    },
  ],
};

// Section 2 — Continuous Compliance, seven-step chain
const CARD_TITLE = 'Northstar Landscaping · Meal & rest breaks';

export const ContinuousCompliance = {
  eyebrow: 'Continuous compliance',
  heading: 'Compliant every day, as the rules around your business change.',
  lede: 'The rules change. Your business changes. Bambee keeps your compliance current through both: gaps found, fixed, and verified. Nothing piles up and no one who comes checking catches you off guard.',
  steps: [
    {
      tab: 'Requirement',
      kicker: 'What Bambee knows',
      title: 'Know what applies.',
      body: 'Which federal, state, municipal, industry, and company conditions create applicable requirements for this company.',
      quote: '“I don’t have to continuously determine what applies myself.”',
      product: 'Bright Compliance',
      card: {
        title: CARD_TITLE,
        rows: [
          {
            label: 'Governing requirement',
            value: 'CA Labor Code §512',
            tone: 'purple',
          },
          { label: 'Jurisdiction', value: 'California', tone: 'grey' },
          {
            label: 'Company elements affected',
            value: '1 policy · 2 practices',
            tone: 'grey',
          },
          { label: 'Monitoring', value: 'Continuous', tone: 'green' },
        ],
        note: 'Requirements are tracked as conditions change, not reviewed once a year.',
      },
    },
    {
      tab: 'Policy',
      kicker: 'What Bambee does',
      title: 'Connect the requirement to the standard.',
      body: 'Policies can be created, maintained, and updated as applicable requirements change. Your Smart Handbook doesn’t just document the company. It stays connected to what governs it.',
      quote: '“My handbook stops going stale the moment it’s written.”',
      product: 'Smart Handbook',
      card: {
        title: CARD_TITLE,
        rows: [
          { label: 'Requirement changed', value: 'Detected', tone: 'purple' },
          {
            label: 'Policy affected',
            value: 'Meal & rest breaks',
            tone: 'grey',
          },
          { label: 'Revision', value: 'v4 created', tone: 'purple' },
          { label: 'Company standard', value: 'Updated', tone: 'green' },
        ],
        note: 'The handbook is the company standard, kept aligned with what governs it.',
      },
    },
    {
      tab: 'People',
      kicker: 'What Bambee does',
      title: 'Move the standard into the workforce.',
      body: 'Bambee determines who the policy or requirement applies to and helps move the standard to the people it governs.',
      quote: '“A policy isn’t implemented merely because it exists.”',
      product: 'Smart Handbook',
      card: {
        title: CARD_TITLE,
        rows: [
          { label: 'Affected employees', value: '12', tone: 'grey' },
          { label: 'Acknowledged', value: '9', tone: 'green' },
          { label: 'Outstanding', value: '3', tone: 'amber' },
          { label: 'Follow-up', value: 'Bambee working it', tone: 'solid' },
        ],
        note: 'Three people still require follow-up. Bambee is on it.',
      },
    },
    {
      tab: 'Practice',
      kicker: 'What Bambee does',
      title: 'Turn the written standard into behavior.',
      body: 'Bambee connects the standard to what employees and managers actually need to do. This is where compliance becomes behavior.',
      quote:
        '“Compliance lives in what the company does, not merely what the handbook says.”',
      product: 'Bright Compliance',
      card: {
        title: CARD_TITLE,
        rows: [
          {
            label: 'Policy requires',
            value: 'Break log per shift',
            tone: 'purple',
          },
          {
            label: 'Manager practice',
            value: 'Weekly verification',
            tone: 'grey',
          },
          { label: 'Employee action', value: 'Log at shift end', tone: 'grey' },
          { label: 'Activity observed', value: 'Partial', tone: 'amber' },
        ],
        note: 'Expected activity is compared with the activity that actually occurs.',
      },
    },
    {
      tab: 'Gap',
      kicker: 'What Bambee does',
      title: 'Find the difference between expected and actual.',
      body: 'Bambee compares the expected state with the actual state and identifies where the two differ.',
      quote: '“I don’t have to discover every compliance problem myself.”',
      product: 'Bright Compliance',
      card: {
        title: CARD_TITLE,
        rows: [
          {
            label: 'Expected',
            value: 'Manager verifies breaks weekly',
            tone: 'grey',
          },
          {
            label: 'Actual',
            value: 'Not completed for 60 days',
            tone: 'amber',
          },
          { label: 'Gap', value: 'Correction required', tone: 'solid' },
          { label: 'Exposure', value: 'Elevated', tone: 'amber' },
        ],
        note: 'Bambee knows what should be true, knows what is actually true, and can identify the difference.',
      },
    },
    {
      tab: 'Correction',
      kicker: 'What Bambee does',
      title: 'Get the company back into alignment.',
      body: 'Depending on Coverage and plan, Bambee can work with the people involved: reach out, explain, coordinate, coach, follow up, correct.',
      quote:
        '“Don’t just tell me we’re out of compliance. Help get us back into compliance.”',
      product: 'Bright Compliance',
      card: {
        title: CARD_TITLE,
        rows: [
          { label: 'Engaged', value: 'Marcus (manager)', tone: 'purple' },
          {
            label: 'Explained',
            value: 'What the practice requires',
            tone: 'grey',
          },
          { label: 'Coached', value: 'Verification workflow', tone: 'grey' },
          { label: 'Follow-up', value: 'Scheduled · 7 days', tone: 'solid' },
        ],
        note: 'Bambee is visibly doing the correction work, not assigning it back to the founder.',
      },
    },
    {
      tab: 'Verification',
      kicker: 'What Bambee does',
      title: 'Check that it actually happened.',
      body: 'Bambee checks that the correction occurred and continues observing the underlying state afterward.',
      quote: '“The company doesn’t quietly fall back out of compliance.”',
      product: 'Bright Compliance',
      card: {
        title: CARD_TITLE,
        rows: [
          { label: 'Gap', value: 'Closed', tone: 'green' },
          { label: 'Required state', value: 'Restored', tone: 'green' },
          { label: 'Evidence', value: 'Recorded', tone: 'grey' },
          { label: 'Observation', value: 'Continuous', tone: 'solid' },
        ],
        note: 'Verified, recorded, and still watched.',
      },
    },
  ],
};

// Section 3 — Company Coverage
export const CompanyCoverage = {
  eyebrow: 'First principle · 01',
  heading: 'Bambee Company Coverage™',
  emphasisLead: 'Bambee covers enough of your company to operate it.',
  body: 'After a decade running HR for small businesses, Bambee learned why traditional HR fails: it covers too little of the company. It waits to be told about problems, sees only fragments of what’s happening, and hands the work back to the founder to do.',
  emphasisTail: 'You can’t operate what you don’t cover. So Bambee covers it.',
  table: {
    title: 'The company, progressively covered',
    rows: [
      { label: 'Visibility', value: 'See more of the company.' },
      {
        label: 'Relationships',
        value: 'Work directly with more of its people.',
      },
      {
        label: 'Responsibility',
        value: 'Take responsibility for more of the work.',
      },
    ],
  },
};

// Section 4 — Who does the work
export const WhoDoesTheWork = {
  eyebrow: 'Who does the work',
  heading: 'How much stays on your plate is up to you.',
  lede: 'Coverage grows in three steps. Each one moves more of the workforce operation from your plate to Bambee’s.',
  levels: [
    {
      name: 'Visibility',
      body: 'Bambee watches your company, flags what needs attention, and tells you.',
      share: 30,
    },
    {
      name: 'Relationships',
      body: 'Bambee works directly with your managers and people on what it finds.',
      share: 55,
    },
    {
      name: 'Responsibility',
      body: 'Bambee owns defined practices and issues, start to finish.',
      share: 76,
    },
  ],
};

// Section 5 — Good Company, four-stage flow
export const GoodCompany = {
  eyebrow: 'Good Company',
  heading: 'One issue, followed all the way to done.',
  lede: 'When something in your company stops happening the way it should, here’s what Bambee does about it.',
  stages: [
    {
      chip: 'Found',
      title: 'Bambee finds the gap.',
      body: 'A required meal-break check quietly stopped 60 days ago. Nobody reported it. Bambee spots it against the company’s own policy and opens the correction.',
      card: {
        title: 'Bright Compliance · Issue #24819',
        rows: [
          { label: 'Requirement', value: 'CA §512', tone: 'grey' },
          { label: 'Practice status', value: 'Missed 60 days', tone: 'amber' },
          { label: 'Manager responsible', value: 'Marcus', tone: 'grey' },
          { label: 'Status', value: 'Gap found · flagged', tone: 'solid' },
        ],
      },
    },
    {
      chip: 'Worked',
      title: 'Bambee works it with the manager.',
      body: 'Marcus gets coached on the practice, prepared for the conversation with Jordan, and followed up on until it’s done. The founder isn’t needed.',
      card: {
        title: 'Good Company Mobile · Issue #24819',
        rows: [
          { label: 'Engaged', value: 'Marcus (manager)', tone: 'purple' },
          { label: 'Practice', value: 'Restarted', tone: 'green' },
          { label: 'Prep', value: '1:1 agenda delivered', tone: 'grey' },
          { label: 'Founder', value: 'Not required', tone: 'green' },
        ],
      },
    },
    {
      chip: 'Decided',
      title: 'Bambee prepares the decision.',
      body: 'When the issue turns consequential, Bambee lays out what’s known, what’s missing, the real options, and what it recommends. You still make the call that’s yours to make.',
      card: {
        title: 'Smart Path · Issue #24819',
        rows: [
          { label: 'Issue', value: 'Performance · Jordan R.', tone: 'grey' },
          {
            label: 'We know',
            value: 'Two documented conversations',
            tone: 'grey',
          },
          { label: 'Missing', value: 'Policy acknowledgment', tone: 'amber' },
          {
            label: 'Options',
            value: 'Coach · Correct · Terminate',
            tone: 'grey',
          },
          {
            label: 'Recommended',
            value: 'Final corrective action',
            tone: 'solid',
          },
        ],
      },
    },
    {
      chip: 'Measured',
      title: 'Bambee checks whether it worked.',
      body: 'After the fix, Bambee compares what was intended with what actually happened, and feeds the difference into the next decision.',
      card: {
        title: 'Better Teams · Issue #24819',
        rows: [
          { label: 'Intervention', value: 'Manager coaching', tone: 'grey' },
          { label: 'Intended', value: '+15%', tone: 'grey' },
          { label: 'Actual', value: '+11%', tone: 'purple' },
          { label: 'Next', value: 'Continue · re-measure', tone: 'solid' },
        ],
      },
    },
  ],
};

// Section 6 — Coverage in action
export const CoverageInAction = {
  eyebrow: 'Company Coverage in action',
  headingLines: [
    'Bambee sees it. Bambee reaches the right people.',
    'Bambee does the work.',
  ],
  scenarios: [
    {
      title: 'The right person shows up for the situation.',
      happened:
        'Priya asked for eight weeks of leave, and three different rules apply to her.',
      does: [
        'Bambee puts an HR Manager on the leave question.',
        'Bambee works out which federal, state and city rules apply.',
        'Bambee tells the manager what he can and cannot say.',
        'Bambee handles the paperwork and the dates.',
      ],
      quote: 'You don’t learn leave law to answer one employee’s question.',
      card: {
        title: 'Bambee team · Leave request · Priya N.',
        team: [
          {
            role: 'HR Manager',
            status: 'On this',
            active: true,
            detail: 'Judgment on the leave rules and what to do',
          },
          {
            role: 'Staff Coordinator',
            status: 'On this',
            active: true,
            detail: 'Paperwork, dates and follow-through',
          },
          {
            role: 'Chief of Staff',
            status: 'Standing by',
            active: false,
            detail: 'Manager expectations and coaching',
          },
          {
            role: 'Coverage Advisor',
            status: 'Standing by',
            active: false,
            detail: 'What Bambee sees and where it can do more',
          },
          {
            role: 'Super Support',
            status: 'Standing by',
            active: false,
            detail: 'Everyday questions, fast',
          },
        ],
        note: 'The capability enters the work. It doesn’t become another subject you have to learn.',
      },
    },
    {
      title: 'Bambee works out what should happen next.',
      happened: 'A manager wants to fire Taylor after months of problems.',
      does: [
        'Bambee pulls together the history and the documentation.',
        'Bambee finds what’s missing before anything is said.',
        'Bambee lays out the real options and the risk in each.',
        'Bambee tells you which one it recommends and why.',
      ],
      quote: 'You don’t guess, and you don’t decide it alone in a hallway.',
      card: {
        title: 'Smart Path · Potential termination · Taylor M.',
        rows: [
          {
            label: 'We know',
            value: 'Two conversations, one written warning',
            tone: 'grey',
          },
          {
            label: 'Missing',
            value: 'No acknowledgment on current policy',
            tone: 'amber',
          },
          {
            label: 'Option A',
            value: 'Keep coaching, recovery uncertain',
            tone: 'grey',
          },
          {
            label: 'Option B',
            value: 'Final corrective action, recommended',
            tone: 'solid',
          },
          {
            label: 'Option C',
            value: 'Terminate now, higher risk today',
            tone: 'grey',
          },
        ],
        note: 'Bambee prepares the decision.',
      },
    },
    {
      title: 'You hear what matters. Not everything.',
      happened: 'Two things moved in your workforce this week.',
      does: [
        'Bambee tells you what changed, in plain language.',
        'Bambee tells you what it’s already doing about it.',
        'Bambee tells you the one thing that may need you later.',
        'Bambee brings escalations already worked out.',
      ],
      quote:
        'You stop being the person every workforce question travels through.',
      card: {
        title: 'Good Company Mobile · Your weekly brief',
        rows: [
          {
            label: 'This week',
            value: 'Two things moved in your workforce',
            tone: 'grey',
          },
          { label: 'Bambee', value: 'Already working both', tone: 'solid' },
          {
            label: 'You',
            value: 'One possible decision in ten days',
            tone: 'purple',
          },
        ],
        note: 'One brief. One possible decision in ten days.',
      },
    },
  ],
  cta: {
    text: 'Give Bambee more of your company to handle.',
    label: 'See the plans →',
    href: '#plans',
  },
};

// Section 7 — Organizational Matters
export const OrganizationalMatters = {
  eyebrow: 'First principle · 02',
  heading: 'Organizational Matters™',
  emphasisLead: 'With Bambee, nothing slips through the cracks.',
  body: 'The complaint nobody wrote down. The leave question answered in a hallway. The manager who quietly stopped doing check-ins. The ones that slip are the ones that come back. Bambee makes every one of them a Matter: identified, owned, worked to an outcome, and recorded.',
  emphasisTail:
    'Bambee gives workforce work an owner, a process, and an outcome.',
  table: {
    title: 'Every issue, somewhere to go',
    rows: [
      {
        label: 'Identified',
        value: 'Seen and written down, the moment it surfaces.',
      },
      { label: 'Owned', value: 'Someone responsible from day one.' },
      { label: 'Worked', value: 'Moved forward until something happens.' },
      { label: 'Resolved', value: 'Outcome reached, recorded, and kept.' },
    ],
  },
};

// Section 8 — Better Teams
export const BetterTeams = {
  eyebrow: 'Better Teams',
  heading: 'Bambee handled it. But did it work?',
  body: [
    'Resolved and recorded isn’t the finish line. Bambee measures what actually happened next.',
    'Most HR stops at handled. Bambee compares what was intended with what actually happened, every time, and the difference decides what happens next.',
  ],
  emphasis:
    'Nothing stops at handled. Bambee measures what actually happened, and makes it better.',
  card: {
    title: 'Better Teams · Policy correction',
    intro:
      'The break policy was updated and moved into the workforce. Did actual practice follow the corrected standard?',
    rows: [
      { label: 'Intervention', value: 'Policy correction', tone: 'grey' },
      { label: 'Intended', value: '100% adherence', tone: 'grey' },
      { label: 'Actual', value: '94%', tone: 'purple' },
      { label: 'Gap', value: 'Two crews drifting', tone: 'amber' },
      { label: 'Next', value: 'Re-verify weekly', tone: 'solid' },
    ],
    note: 'The measurement decides the next move.',
  },
  tilesTitle: 'Every intervention, measured the same way',
  tiles: [
    {
      label: 'Manager coaching',
      value: '+11%',
      against: 'vs +15% intended',
      next: 'Continue · re-measure',
    },
    {
      label: 'Corrective action',
      value: '2 of 3',
      against: 'criteria met',
      next: 'Hold the standard · re-check at 30 days',
    },
    {
      label: 'Performance plan',
      value: '+17%',
      against: 'vs +20% intended',
      next: 'Extend 15 days · on track',
    },
  ],
};

// Section 9 — What you get back
export const WhatYouGetBack = {
  eyebrow: 'What you get back',
  heading: 'Measure what Bambee gives back, and what the founder does with it.',
  stats: [
    {
      tone: 'a1',
      label: 'Before Bambee',
      figure: '11 hrs',
      unit: 'founder workforce load / week',
      body: ['Checking. Answering.', 'Routing. Chasing and correcting.'],
    },
    {
      tone: 'a2',
      label: 'Returned',
      figure: '5 hrs',
      unit: 'back to the founder / week',
      body: ['More workforce work moves', 'without founder involvement.'],
    },
    {
      tone: 'a3',
      label: 'Redeployed',
      figure: '3 + 2',
      unit: 'hours, back in the business',
      body: ['3 hrs → customers / revenue', '2 hrs → product'],
    },
    {
      tone: 'a4',
      label: 'Result',
      figure: '2',
      unit: 'priorities actually moved',
      body: ['Pipeline moved.', 'Product milestone advanced.'],
    },
  ],
  dimensions: [
    {
      label: 'Availability',
      heading: 'How much founder capacity became available?',
      body: 'When Bambee takes more workforce work off the founder, founder attention becomes available for something else. Availability is the most literal measure in the model: hours and interactions that no longer require you.',
      before: '11.4 hrs',
      after: '6.8 hrs',
      delta: '+4.6 hrs',
      note: 'Availability measures what the founder gets back.',
    },
    {
      label: 'Amplification',
      heading: 'How much farther does founder intent travel?',
      body: 'Founders set standards: how managers should manage, how people should be treated, what good looks like. That intent usually travels only as far as the founder can personally inspect. Bambee translates it into policies, practices, and manager follow-up.',
      before: '38%',
      after: '81%',
      delta: '+43 pts',
      note: 'Amplification measures the reach of founder intent.',
    },
  ],
  footnote:
    'Alongside availability and amplification, Bambee tracks acceleration, leverage, and investment: what the founder gets back, and what the business does with it.',
};

/**
 * Section 10 — Plans (BIO-2157).
 *
 * No price lives here. Each column names a `productKey`; usePlanPrices reads
 * the tier-1 (1–19) monthly amount from the Recurly catalog, and a key the
 * catalog does not return renders without a price. Each column is the cheaper
 * plan at its level, which is what makes "starting from" true.
 */
export const Plans = {
  eyebrow: 'Plans',
  heading: 'How much do you want Bambee to take off your plate?',
  tiers: [
    {
      tone: 'p0',
      productKey: 'COMPANY_OBSERVATION',
      name: 'Observation Plans · Visibility',
      body: 'Bambee continuously understands more of your company and workforce, detects what deserves attention, develops what it means, and keeps you informed.',
      promiseLead: 'Know what’s happening',
      promiseTail: ' without having to discover everything yourself.',
      cta: 'Start with Observation',
    },
    {
      tone: 'p1',
      productKey: 'STANDARD_SUPERVISION',
      name: 'Supervision Plans · Relationships',
      body: 'Bambee works directly with your people and helps keep standards, practices, managers, compliance, and required activity operating the way they should.',
      promiseLead: 'Don’t just know what should happen.',
      promiseTail: ' Bambee makes sure it happens.',
      cta: 'Add Supervision',
    },
    {
      tone: 'p2',
      productKey: 'WORKFORCE_PRACTICE_MANAGEMENT',
      name: 'Management Plans · Responsibility',
      body: 'Bambee takes responsibility for defined workforce practices and Organizational Matters, and performs the actual work required to move them to outcome.',
      promiseLead: 'Hand it over.',
      promiseTail: ' Give Bambee more of the workforce operation to own.',
      cta: 'Explore Management',
    },
  ],
  startingFrom: 'starting from',
  interval: '/month',
  footLead: 'Delegation and Optimization coming 2027.',
  footTail: 'Compare Plans & Pricing →',
  footHref: '/plans',
  // /plans band selector (BIO-2167).
  bandsLabel: 'Number of employees',
  bandsUnit: 'employees',
};

// Section 11 — Close band
export const CloseBand = {
  eyebrow: 'Built for small business',
  heading: 'Ready to put your HR on autopilot?',
  body: 'Bambee can continuously understand more of what’s happening across your workforce, work directly with more of your people, and take progressively greater responsibility for keeping the operation moving. HR should work this hard for every company.',
  cta: 'Get Bambee →',
  // Bound to the Observation tier-1 price at runtime; never a literal amount.
  price: (amount) => `Plans starting from ${amount}/month.`,
};

/**
 * Plan and close-band CTA clicks (BIO-2412). Never `Lead`: Segment forwards
 * `Lead` to Meta, and only the contact info submit is a lead.
 */
export const RegistrationStartedEvent = 'registration-started';

export default {
  BetterTeams,
  BSideHeroContent,
  BSideReasonOptions,
  ManagerRow,
  CloseBand,
  CompanyCoverage,
  ContinuousCompliance,
  CoverageInAction,
  GoodCompany,
  OrganizationalMatters,
  Plans,
  WhatYouGetBack,
  WhoDoesTheWork,
  WhyBambee,
};
